import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { APIProvider, Map, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { CheckCircle2, MapPin, ChevronDown } from 'lucide-react';
import { ImageCarousel } from '@/components/ImageCarousel';
import { MapErrorBoundary } from '@/components/MapErrorBoundary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Deterministic small offset from listing id so approximate locations vary but don't move
const approximatePosition = (listing) => {
  const seed = (listing.id || '').split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  const jitterLat = (((seed % 100) / 100) - 0.5) * 0.006; // ~ +/- 300m
  const jitterLng = ((((seed * 7) % 100) / 100) - 0.5) * 0.006;
  return {
    lat: (listing.lat || -33.925) + jitterLat,
    lng: (listing.lng || 18.410) + jitterLng,
  };
};

const priceFrom = (listing) => {
  if (listing.price_from) return listing.price_from;
  const map = listing.monthly_prices || {};
  const values = Object.values(map).filter((v) => typeof v === 'number' && v > 0);
  if (values.length > 0) return Math.min(...values);
  return listing.monthly_price;
};

const ListingCard = ({ listing, onClick, onMouseEnter, onMouseLeave }) => {
  const from = priceFrom(listing);
  return (
    <div
      className="group cursor-pointer transition-all duration-300"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-[10px] bg-paper-2 mb-4">
        <ImageCarousel photos={listing.photos || []} alt={listing.title} />
        {listing.verification_status === 'approved' && (
          <div className="absolute top-3 left-3 z-10 bg-white rounded-md px-2.5 py-1.5 flex items-center gap-1.5 pointer-events-none">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} style={{ color: 'var(--sea)' }} />
            <span className="text-[11px] tracking-[0.1em] uppercase font-bold" style={{ color: 'var(--sea)' }}>Checked</span>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <div className="text-[13.5px] text-muted mb-1">
          {listing.neighbourhood}
          {listing.place_type === 'private_room' && <span className="ml-2">· Room</span>}
        </div>
        <h3 className="font-display text-[19px] tracking-[-0.02em] text-ink leading-tight line-clamp-2">
          {listing.title}
        </h3>
        <div className="flex items-center gap-2 text-[13.5px] text-muted pt-1">
          <span>{listing.bedrooms} bed</span>
          <span>·</span>
          <span>{listing.bathrooms} bath</span>
          {listing.wifi_speed && (
            <>
              <span>·</span>
              <span>{listing.wifi_speed}Mbps</span>
            </>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 pt-3 mt-1 border-t" style={{ borderColor: 'var(--line)' }}>
          <div className="font-display text-[19px] font-semibold">
            R{Number(from).toLocaleString('en-ZA')}
            <span className="ml-1 font-sans text-[13px] font-normal text-muted">/ month</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MapMarker = ({ listing, isSelected, onClick }) => {
  const pos = approximatePosition(listing);
  const from = priceFrom(listing);
  return (
    <AdvancedMarker position={pos} onClick={onClick}>
      <div
        className={`px-3 py-1.5 font-semibold text-sm shadow-lg transition-all cursor-pointer ${
          isSelected
            ? 'bg-ink text-paper scale-110'
            : 'bg-paper text-ink hover:scale-105'
        }`}
      >
        R{(from / 1000).toFixed(0)}k
      </div>
    </AdvancedMarker>
  );
};

const AMENITY_FILTERS = [
  { key: 'pet_friendly', label: 'Pets welcome' },
  { key: 'air_conditioning', label: 'Air conditioning' },
  { key: 'security', label: 'Security' },
  { key: 'pool', label: 'Pool' },
  { key: 'parking', label: 'Parking' },
  { key: 'workspace', label: 'Workspace' },
  { key: 'gym', label: 'Gym' },
  { key: 'balcony', label: 'Balcony' },
  { key: 'laundry', label: 'Laundry' },
  { key: 'kitchen', label: 'Full kitchen' },
];

const MONTHS = (() => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en', { month: 'short', year: 'numeric' });
    months.push({ value, label });
  }
  return months;
})();

export const Listings = () => {
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredListing, setHoveredListing] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const initialMonths = useMemo(() => {
    const raw = searchParams.get('months');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);
  const initialNeighbourhoods = useMemo(() => {
    const raw = searchParams.get('neighbourhoods');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);
  const [selectedNeighbourhoods, setSelectedNeighbourhoods] = useState(initialNeighbourhoods);
  const [selectedMonths, setSelectedMonths] = useState(initialMonths);
  const [placeType, setPlaceType] = useState(searchParams.get('place_type') || ''); // '', 'entire_place', 'private_room'
  const [filters, setFilters] = useState({
    neighbourhood: searchParams.get('neighbourhood') || '',
    min_price: '',
    max_price: '',
    bedrooms: '',
    pet_friendly: false,
    air_conditioning: false,
    security: false,
    pool: false,
    parking: false,
    workspace: false,
    gym: false,
    balcony: false,
    laundry: false,
    kitchen: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.neighbourhood, selectedNeighbourhoods, selectedMonths, placeType]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('verified_only', 'true');
      if (selectedNeighbourhoods.length > 0) {
        params.append('neighbourhoods', selectedNeighbourhoods.join(','));
      } else if (filters.neighbourhood) {
        params.append('neighbourhood', filters.neighbourhood);
      }
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
      if (placeType) params.append('place_type', placeType);
      if (selectedMonths.length > 0) params.append('available_months', selectedMonths.join(','));
      AMENITY_FILTERS.forEach(({ key }) => {
        if (filters[key]) params.append(key, 'true');
      });

      const { data } = await axios.get(`${API}/listings?${params.toString()}`);
      setListings(data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMonth = (monthValue) => {
    setSelectedMonths((prev) =>
      prev.includes(monthValue) ? prev.filter((m) => m !== monthValue) : [...prev, monthValue]
    );
  };

  const applyFilters = () => {
    fetchListings();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      neighbourhood: '',
      min_price: '',
      max_price: '',
      bedrooms: '',
      pet_friendly: false,
      air_conditioning: false,
      security: false,
      pool: false,
      parking: false,
      workspace: false,
      gym: false,
      balcony: false,
      laundry: false,
      kitchen: false,
    });
    setSelectedMonths([]);
    setPlaceType('');
    setTimeout(fetchListings, 100);
  };

  const activeFiltersCount = AMENITY_FILTERS.filter(({ key }) => filters[key]).length +
    (filters.min_price ? 1 : 0) + (filters.max_price ? 1 : 0) + (filters.bedrooms ? 1 : 0) +
    (placeType ? 1 : 0) + selectedMonths.length + (selectedNeighbourhoods.length > 0 ? 1 : 0);

  const areaLabel = searchParams.get('area');

  const clearAreaFilter = () => {
    setSelectedNeighbourhoods([]);
    setFilters((f) => ({ ...f, neighbourhood: '' }));
    // strip both params from URL so refreshing doesn't re-apply
    navigate('/listings', { replace: true });
  };

  const mapCenter = { lat: -33.925, lng: 18.410 };
  const neighbourhoods = ['Sea Point', 'Camps Bay', 'V&A Waterfront', 'Gardens', 'Green Point'];

  return (
    <div className="min-h-screen bg-paper">
      {/* Filter Bar (non-sticky) */}
      <div className="border-b bg-paper px-4 md:px-12 py-4" data-testid="filter-bar" style={{ borderBottomColor: 'var(--line)' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-3 md:gap-4">
          <div className="flex items-center gap-2 flex-wrap overflow-x-auto -mx-1 px-1 pb-1 md:pb-0 scrollbar-hide">
            {areaLabel && selectedNeighbourhoods.length > 0 && (
              <button
                onClick={clearAreaFilter}
                className="pill on flex items-center gap-2 group hover:opacity-90 transition-opacity"
                data-testid="area-filter-pill"
                title="Clear area filter"
              >
                <span>{areaLabel}</span>
                <span className="opacity-70 group-hover:opacity-100 text-[14px] leading-none">×</span>
              </button>
            )}
            {neighbourhoods.map((n) => (
              <button
                key={n}
                onClick={() => {
                  // Tapping a single chip is exclusive — clear any area-group filter.
                  setSelectedNeighbourhoods([]);
                  setFilters({ ...filters, neighbourhood: filters.neighbourhood === n ? '' : n });
                }}
                className={`px-3 md:px-4 py-1.5 text-xs md:text-sm border transition-colors whitespace-nowrap ${
                  filters.neighbourhood === n && selectedNeighbourhoods.length === 0
                    ? 'bg-ink text-paper border-ink'
                    : 'bg-transparent text-ink border-gray-400 hover:border-ink'
                }`}
                data-testid={`filter-${n.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <div className="flex border border-gray-400">
              <button
                onClick={() => setPlaceType(placeType === 'entire_place' ? '' : 'entire_place')}
                className={`px-2.5 md:px-3 py-1.5 text-[10px] md:text-xs caps transition-colors ${
                  placeType === 'entire_place' ? 'bg-ink text-paper' : 'bg-transparent text-ink hover:bg-ink/5'
                }`}
                data-testid="filter-place-entire"
              >
                Entire place
              </button>
              <button
                onClick={() => setPlaceType(placeType === 'private_room' ? '' : 'private_room')}
                className={`px-2.5 md:px-3 py-1.5 text-[10px] md:text-xs caps border-l border-gray-400 transition-colors ${
                  placeType === 'private_room' ? 'bg-ink text-paper' : 'bg-transparent text-ink hover:bg-ink/5'
                }`}
                data-testid="filter-place-room"
              >
                Room
              </button>
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              size="sm"
              className="border-ink text-ink hover:bg-ink hover:text-paper rounded-none caps bg-transparent text-[10px]"
              data-testid="listings-filter-btn"
            >
              Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              <ChevronDown className={`ml-2 w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} strokeWidth={2} />
            </Button>
            <div className="text-xs md:text-sm text-muted caps text-[10px] ml-auto md:ml-0" data-testid="listings-count">
              {loading ? '...' : `${listings.length} homes`}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="border-b border-gray-300 bg-cream-darker p-6 md:px-12" data-testid="filter-panel">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <Label className="label-caps text-gray-700 mb-2 block">Min Price (R)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.min_price}
                  onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                  className="rounded-none border-gray-400 bg-cream"
                  data-testid="filter-min-price"
                />
              </div>
              <div>
                <Label className="label-caps text-gray-700 mb-2 block">Max Price (R)</Label>
                <Input
                  type="number"
                  placeholder="100000"
                  value={filters.max_price}
                  onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                  className="rounded-none border-gray-400 bg-cream"
                  data-testid="filter-max-price"
                />
              </div>
              <div>
                <Label className="label-caps text-gray-700 mb-2 block">Bedrooms</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      onClick={() => setFilters({ ...filters, bedrooms: filters.bedrooms === String(n) ? '' : String(n) })}
                      className={`w-full py-2 border text-sm transition-colors ${
                        filters.bedrooms === String(n)
                          ? 'bg-black text-white border-black'
                          : 'border-gray-400 hover:border-black'
                      }`}
                      data-testid={`filter-bedrooms-${n}`}
                    >
                      {n}+
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <Label className="label-caps text-gray-700 mb-3 block">Available Months <span className="normal-case text-muted italic font-serif text-xs">(any that work for you)</span></Label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {MONTHS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => toggleMonth(m.value)}
                    className={`p-2 text-xs border transition-colors ${
                      selectedMonths.includes(m.value)
                        ? 'bg-ink text-paper border-ink'
                        : 'border-gray-400 hover:border-ink'
                    }`}
                    data-testid={`month-filter-${m.value}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <Label className="label-caps text-gray-700 mb-3 block">Amenities</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {AMENITY_FILTERS.map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={filters[key]}
                      onCheckedChange={(v) => setFilters({ ...filters, [key]: v })}
                      className="rounded-none border-gray-500"
                      data-testid={`filter-${key}`}
                    />
                    <Label htmlFor={key} className="text-sm text-gray-800 cursor-pointer">{label}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={applyFilters}
                className="bg-black hover:bg-gray-800 text-white rounded-none label-caps"
                data-testid="filter-apply"
              >
                Apply Filters
              </Button>
              <Button
                variant="outline"
                onClick={clearFilters}
                className="border-black text-black hover:bg-black hover:text-white rounded-none label-caps bg-transparent"
                data-testid="filter-clear"
              >
                Clear All
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Split Layout */}
      <div className="flex" style={{ minHeight: 'calc(100vh - 128px)' }}>
        <div className="w-full lg:w-3/5 overflow-y-auto">
          <div className="px-6 md:px-12 py-8">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-black"></div>
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-24" data-testid="listings-empty-state">
                <p className="text-gray-500 mb-4 font-serif italic text-xl">No properties match your criteria</p>
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="border-black text-black hover:bg-black hover:text-white rounded-none label-caps bg-transparent"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {listings.map((listing) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    isHovered={hoveredListing === listing.id}
                    onMouseEnter={() => setHoveredListing(listing.id)}
                    onMouseLeave={() => setHoveredListing(null)}
                    onClick={() => navigate(`/listings/${listing.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="hidden lg:block w-2/5 sticky top-16 bg-gray-200" style={{ height: 'calc(100vh - 64px)' }}>
          {GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY !== 'YOUR_GOOGLE_MAPS_API_KEY' ? (
            <MapErrorBoundary
              fallback={
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 p-8" data-testid="map-fallback">
                  <MapPin className="w-10 h-10 text-gray-400 mb-3" strokeWidth={1.2} />
                  <p className="text-sm text-muted text-center mb-1 font-serif italic">Map preview unavailable</p>
                  <p className="text-[10px] text-muted caps text-center">Enable Maps JavaScript API in Google Cloud</p>
                </div>
              }
            >
              <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <Map
                  style={{ width: '100%', height: '100%' }}
                  defaultCenter={mapCenter}
                  defaultZoom={12}
                  mapId="cape-town-monthly-map"
                  gestureHandling="greedy"
                  mapTypeControl={false}
                  streetViewControl={false}
                  fullscreenControl={false}
                >
                  {listings.map((listing) => (
                    <MapMarker
                      key={listing.id}
                      listing={listing}
                      isSelected={hoveredListing === listing.id || selectedListing?.id === listing.id}
                      onClick={() => setSelectedListing(listing)}
                    />
                  ))}
                  {selectedListing && (
                    <InfoWindow
                      position={approximatePosition(selectedListing)}
                      onCloseClick={() => setSelectedListing(null)}
                    >
                      <div className="p-2 max-w-[220px]">
                        <div className="font-semibold text-sm mb-1">{selectedListing.title}</div>
                        <div className="text-xs text-gray-600 mb-2">{selectedListing.neighbourhood}</div>
                        <div className="text-sm font-bold">From R{Number(priceFrom(selectedListing)).toLocaleString()}/mo</div>
                      </div>
                    </InfoWindow>
                  )}
                </Map>
              </APIProvider>
            </MapErrorBoundary>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-cream-darker p-8" data-testid="map-placeholder">
              <MapPin className="w-12 h-12 text-gray-400 mb-4" strokeWidth={1} />
              <p className="text-gray-600 text-center mb-2 font-serif italic text-lg">Interactive Map</p>
              <p className="text-xs text-gray-500 text-center mb-6 label-caps max-w-xs">
                Add Google Maps API key to enable
              </p>
              <div className="space-y-2 w-full max-w-xs">
                {listings.map((listing) => (
                  <div
                    key={listing.id}
                    className="flex items-center justify-between p-3 bg-paper border border-gray-300 hover:border-ink transition-colors cursor-pointer"
                    onClick={() => navigate(`/listings/${listing.id}`)}
                  >
                    <div className="text-xs font-medium truncate">{listing.neighbourhood}</div>
                    <div className="font-serif text-sm font-bold whitespace-nowrap ml-2">
                      R{(priceFrom(listing) / 1000).toFixed(0)}k
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
